import { api } from '@/lib/api'
import { ENDPOINTS } from '@/constants/endpoints'
import { STORAGE_KEYS } from '@/constants/auth'
import type { LoginWith2FAResponse } from '@/types/user'

export interface LoginCredentials {
  username: string
  password: string
}

export interface TokenResponse {
  access: string
  refresh: string
}

export type LoginResult = TokenResponse | LoginWith2FAResponse

function isLoginWith2FA(data: TokenResponse | LoginWith2FAResponse): data is LoginWith2FAResponse {
  return 'requires_2fa' in data && data.requires_2fa === true
}

export const authService = {
  async login(credentials: LoginCredentials): Promise<LoginResult> {
    const response = await api.post<TokenResponse | LoginWith2FAResponse>(
      ENDPOINTS.AUTH.TOKEN,
      credentials
    )
    const data = response.data
    if (isLoginWith2FA(data)) {
      return data
    }
    this.storeTokens(data)
    return data
  },

  async verify2FA(
    temporaryToken: string,
    code: { totp_code: string } | { recovery_code: string }
  ): Promise<TokenResponse> {
    const response = await api.post<TokenResponse>(ENDPOINTS.AUTH.VERIFY_2FA, {
      temporary_token: temporaryToken,
      ...code,
    })
    const tokens = response.data
    this.storeTokens(tokens)
    return tokens
  },

  storeTokens(tokens: TokenResponse): void {
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, tokens.access)
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, tokens.refresh)
  },

  clearTokens(): void {
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN)
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN)
  },

  isAuthenticated(): boolean {
    return !!localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
  },

  getAccessToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)
  },

  getRefreshToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)
  },

  /** refreshes access token, stores new access, throws on failure */
  async refreshAccessToken(): Promise<string> {
    const refresh = this.getRefreshToken()
    if (!refresh) throw new Error('No refresh token')
    const response = await api.post<{ access: string }>(ENDPOINTS.AUTH.REFRESH, { refresh })
    const access = response.data.access
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, access)
    return access
  },
}
